import React from "react";
import Modal from "react-modal";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  resetShowSetLocationModal,
  setDumpsterLocation,
} from "../../dumpstersSlice";
import { getDumpYardTemplates } from "../../../DumpYards/dumpYardsSlice";
import { getOrders } from "../../../Orders/ordersSlice";
import { ReactComponent as Close } from "../../../../assets/images/close.svg";
import { DumpsterLocationType, OrderStatus } from "../../../../models";

import "./SetLocationModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
    maxHeight: "90vh",
    overflow: "auto",
  },
};

interface ComboOption {
  id: string;
  label: string;
  sublabel?: string;
  // Lowercased blob of every searchable field, matched against the query.
  haystack: string;
}

// LocationCombobox is a single search-and-pick field: the input filters the
// option list as you type (name/address/city/zip/phone), and clicking a result
// selects it. Replaces the old separate search box + <select> dropdown.
function LocationCombobox({
  options,
  value,
  onSelect,
  placeholder,
  emptyText,
}: {
  options: ComboOption[];
  value: string;
  onSelect: (id: string) => void;
  placeholder: string;
  emptyText: string;
}) {
  const selected = options.find((o) => o.id === value);
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);

  // Closed with a selection → show its label; otherwise show what's typed.
  const inputValue = open ? query : selected ? selected.label : query;

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.haystack.includes(q));
  }, [options, query]);

  return (
    <div className="location-combobox">
      <input
        type="text"
        className="option-search"
        value={inputValue}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setQuery("");
          setOpen(true);
        }}
        // onMouseDown on the options beats this blur, so a click still selects.
        onBlur={() => setOpen(false)}
      />
      {open && (
        <div className="combobox-list">
          {filtered.length === 0 && (
            <div className="combobox-empty">{emptyText}</div>
          )}
          {filtered.map((o) => (
            <div
              key={o.id}
              className={
                "combobox-option" + (o.id === value ? " selected" : "")
              }
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(o.id);
                setQuery("");
                setOpen(false);
              }}
            >
              <div className="combobox-option-label">{o.label}</div>
              {o.sublabel && (
                <div className="combobox-option-sub">{o.sublabel}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SetLocationModal() {
  const dispatch = useAppDispatch();

  const dumpster = useAppSelector(
    (state) => state.dumpsters.showSetLocationModal
  );
  const setLocationAPI = useAppSelector(
    (state) => state.dumpsters.setDumpsterLocation
  );

  // Storage yards
  const templates = useAppSelector((state) => state.dumpYards.templates) || [];
  const storageYards = React.useMemo(
    () => templates.filter((t) => t.isStorage),
    [templates]
  );

  // Orders with dumpster on site (delivered or scheduled for pickup)
  const orders = useAppSelector((state) => state.orders.orders) || [];
  const activeOrders = React.useMemo(
    () =>
      orders.filter(
        (o) =>
          o.status === OrderStatus.OrderStatusDelivered ||
          o.status === OrderStatus.OrderStatusScheduledToPickup
      ),
    [orders]
  );

  const [locationType, setLocationType] =
    React.useState<DumpsterLocationType>("unknown");
  const [selectedOrderId, setSelectedOrderId] = React.useState<string>("");
  const [selectedStorageId, setSelectedStorageId] = React.useState<string>("");

  // Combobox options: one list for storage, one for orders. The combobox below
  // is a single search-and-pick field — type to filter by name/address/city/
  // zip (e.g. "Monroeville"), then click a result to select it.
  const storageOptions = React.useMemo<ComboOption[]>(
    () =>
      storageYards.map((y) => ({
        id: y.id,
        label: y.name,
        sublabel: `${y.address}, ${y.city} ${y.zip}`.trim(),
        haystack: `${y.name} ${y.address} ${y.city} ${y.zip}`.toLowerCase(),
      })),
    [storageYards]
  );

  const orderOptions = React.useMemo<ComboOption[]>(
    () =>
      activeOrders.map((o) => ({
        id: o.id,
        label: o.name,
        sublabel: `${o.address}, ${o.city} ${o.zip}`.trim(),
        haystack:
          `${o.name} ${o.address} ${o.city} ${o.zip} ${o.phoneNumber}`.toLowerCase(),
      })),
    [activeOrders]
  );

  // Fetch data when modal opens
  React.useEffect(() => {
    if (dumpster) {
      dispatch(getDumpYardTemplates());
      dispatch(
        getOrders({
          pagination: { offset: 0, count: 100 },
          filters: {
            statuses: [
              OrderStatus.OrderStatusDelivered,
              OrderStatus.OrderStatusScheduledToPickup,
            ],
          },
        })
      );

      // Set initial values from dumpster
      setLocationType(dumpster.locationType || "unknown");
      setSelectedOrderId(dumpster.currentOrderId || "");
      setSelectedStorageId(dumpster.currentStorageYardId || "");
    }
  }, [dumpster, dispatch]);

  React.useEffect(() => {
    if (!setLocationAPI.loading && !!setLocationAPI.error) {
      toast.error("Something went wrong. Please try again.");
    }

    if (
      !setLocationAPI.loading &&
      !setLocationAPI.error &&
      setLocationAPI.value
    ) {
      toast.success("Location updated successfully.");
    }
  }, [
    setLocationAPI.loading,
    setLocationAPI.error,
    setLocationAPI.value,
    dispatch,
  ]);

  const handleClose = () => {
    dispatch(resetShowSetLocationModal());
    setLocationType("unknown");
    setSelectedOrderId("");
    setSelectedStorageId("");
  };

  const handleSubmit = () => {
    if (!dumpster) return;

    let address = "";
    let city = "";
    let zip = "";
    let orderId: string | undefined;
    let storageYardId: string | undefined;

    if (locationType === "order" && selectedOrderId) {
      const order = activeOrders.find((o) => o.id === selectedOrderId);
      if (order) {
        address = order.address;
        city = order.city;
        zip = order.zip;
        orderId = order.id;
      }
    } else if (locationType === "storage" && selectedStorageId) {
      const storage = storageYards.find((s) => s.id === selectedStorageId);
      if (storage) {
        address = storage.address;
        city = storage.city;
        zip = storage.zip;
        storageYardId = storage.id;
      }
    }

    dispatch(
      setDumpsterLocation({
        dumpsterId: dumpster.id,
        locationType,
        orderId,
        storageYardId,
        address,
        city,
        zip,
      })
    );
  };

  const isValid = () => {
    if (locationType === "unknown") return true;
    if (locationType === "on_truck") return true;
    if (locationType === "order") return !!selectedOrderId;
    if (locationType === "storage") return !!selectedStorageId;
    return false;
  };

  if (!dumpster) return null;

  const selectedOrder = activeOrders.find((o) => o.id === selectedOrderId);
  const selectedStorage = storageYards.find((s) => s.id === selectedStorageId);

  return (
    <Modal
      isOpen={!!dumpster}
      onRequestClose={handleClose}
      style={modalStyles}
    >
      <div className="set-location-modal-component">
        <Close className="close-button" onClick={handleClose} />

        <div className="title">Set Dumpster Location</div>
        <div className="dumpster-label">{dumpster.label}</div>

        <div className="form-content">
          <div className="field-container">
            <div className="label">Location Type</div>
            <select
              value={locationType}
              onChange={(e) => {
                setLocationType(e.target.value as DumpsterLocationType);
                setSelectedOrderId("");
                setSelectedStorageId("");
              }}
              className="select-input"
            >
              <option value="unknown">Unknown</option>
              <option value="storage">Storage</option>
              <option value="order">Order</option>
              <option value="on_truck">On Truck</option>
            </select>
          </div>

          {locationType === "storage" && (
            <div className="field-container">
              <div className="label">Select Storage</div>
              <LocationCombobox
                options={storageOptions}
                value={selectedStorageId}
                onSelect={setSelectedStorageId}
                placeholder="Search storage by name, address, or zip"
                emptyText="No storage yards match your search."
              />
              {selectedStorage && (
                <div className="selected-address">
                  <div className="address-label">Address:</div>
                  <div className="address-value">
                    {selectedStorage.address}
                    <br />
                    {selectedStorage.city}, {selectedStorage.zip}
                  </div>
                </div>
              )}
            </div>
          )}

          {locationType === "order" && (
            <div className="field-container">
              <div className="label">Select Order</div>
              <LocationCombobox
                options={orderOptions}
                value={selectedOrderId}
                onSelect={setSelectedOrderId}
                placeholder="Search orders by name, address, zip, or phone"
                emptyText="No orders match your search."
              />
              {selectedOrder && (
                <div className="selected-address">
                  <div className="address-label">Address:</div>
                  <div className="address-value">
                    {selectedOrder.address}
                    <br />
                    {selectedOrder.city}, {selectedOrder.zip}
                  </div>
                </div>
              )}
              {activeOrders.length === 0 && (
                <div className="no-options">
                  No active orders with dumpsters on site.
                </div>
              )}
            </div>
          )}

          <div className="button-row">
            <button
              type="button"
              className="cancel-button"
              onClick={handleClose}
              disabled={setLocationAPI.loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="submit-button"
              onClick={handleSubmit}
              disabled={setLocationAPI.loading || !isValid()}
            >
              {setLocationAPI.loading ? (
                <>
                  Saving...
                  <Oval
                    color="#fff"
                    height={16}
                    width={16}
                    secondaryColor="#ccc"
                  />
                </>
              ) : (
                "Save Location"
              )}
            </button>
          </div>

          {!!setLocationAPI.error && (
            <div className="error-text">
              Something went wrong. Please try again.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
